import { toStaffPermissions, type StaffPermission } from '../auth/permissions.js';

import { apiFetch } from './client.js';

/** A back-office role (DEV-67). `compliance` is shown as "AML / MLRO". */
export type AdminRole = 'admin' | 'compliance' | 'client_manager' | 'finance' | 'support';

/** The role on a session: one of the five, or none when the token carries no role this console knows. */
export type SessionRole = AdminRole | 'none';

export interface AdminUserProfile {
  id: string;
  email: string;
  name: string;
  role: SessionRole;
  mfaEnrollmentRequired?: boolean;
  /** From the session token (DEV-112). Absent on a session stored before permissions existed. */
  permissions?: StaffPermission[];
}

// The refresh token never appears here: the gateway sets it as an HttpOnly
// cookie (`norvix_admin_rt`) that this code cannot read (DEV-125).
interface LoginApiResponse {
  adminId?: string;
  email?: string;
  accessToken: string;
  expiresAt: number;
  tokenType: 'Bearer';
  isAdmin?: boolean;
  mfaEnrollmentRequired?: boolean;
}

interface RefreshApiResponse {
  adminId?: string;
  email?: string;
  accessToken: string;
  expiresAt: number;
  tokenType: 'Bearer';
}

/** A signed-in session: the access token (memory only) and who it belongs to. */
export interface SessionResult {
  accessToken: string;
  expiresAt: number;
  user: AdminUserProfile;
}

export type LoginResult = SessionResult;
export type RefreshResult = SessionResult;

interface SessionClaims { sub?: string; roles?: readonly string[]; scope?: string; perms?: unknown }

function decodeJwtPayload(token: string): SessionClaims {
  const parts = token.split('.');
  if (parts.length !== 3 || parts[1] === undefined) {
    return {};
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '==='.slice((base64.length + 3) % 4);
    return JSON.parse(atob(padded)) as SessionClaims;
  } catch {
    return {};
  }
}

/** A role this console does not know grants nothing, rather than a guessed default. */
export function normalizeRole(role: string | undefined): SessionRole {
  switch (role?.toLowerCase()) {
    case 'admin':          return 'admin';
    case 'compliance':     return 'compliance';
    case 'client_manager': return 'client_manager';
    case 'finance':        return 'finance';
    case 'support':        return 'support';
    default:               return 'none';
  }
}

/** Widest first, when a staff member holds several roles. */
const ROLE_PRECEDENCE = ['ADMIN', 'COMPLIANCE', 'CLIENT_MANAGER', 'SUPPORT', 'FINANCE'];

export function sessionPermissions(
  token: string,
): Required<Pick<AdminUserProfile, 'role' | 'mfaEnrollmentRequired' | 'permissions'>> {
  const payload = decodeJwtPayload(token);
  const held = (payload.roles ?? []).map((r) => r.toUpperCase());
  const role = ROLE_PRECEDENCE.find((r) => held.includes(r));
  return {
    role: normalizeRole(role),
    mfaEnrollmentRequired: payload.scope === 'mfa-enrollment',
    permissions: toStaffPermissions(payload.perms),
  };
}

function profileFrom(accessToken: string, email: string): AdminUserProfile {
  const payload = decodeJwtPayload(accessToken);
  return {
    id: payload.sub ?? 'unknown',
    email,
    name: email.split('@')[0] ?? 'Admin',
    ...sessionPermissions(accessToken),
  };
}

export const authApi = {
  async login(email: string, password: string, totpCode?: string): Promise<LoginResult> {
    const body: Record<string, string> = { email, password };
    if (totpCode !== undefined && totpCode.length > 0) body.totpCode = totpCode;
    const resp = await apiFetch<LoginApiResponse>('/admin/auth/login', {
      method: 'POST',
      body,
    });
    const user = profileFrom(resp.accessToken, resp.email ?? email);
    return {
      accessToken: resp.accessToken,
      expiresAt: resp.expiresAt,
      user: { ...user, mfaEnrollmentRequired: resp.mfaEnrollmentRequired === true || user.mfaEnrollmentRequired === true },
    };
  },

  /**
   * Renew from the HttpOnly refresh cookie, which the browser attaches. The
   * gateway rotates the cookie (single use) and returns a new access token
   * plus who it belongs to, so a reloaded page needs nothing stored. JSON
   * body: the gateway refuses what a cross-site form can send.
   */
  async refresh(): Promise<RefreshResult> {
    const resp = await apiFetch<RefreshApiResponse>('/admin/auth/refresh', {
      method: 'POST',
      body: {},
      // Don't trigger the auto-refresh interceptor on this call.
      skipAuthRefresh: true,
    });
    if (
      typeof resp?.accessToken !== 'string' || resp.accessToken === '' ||
      typeof resp.email !== 'string' || resp.email === '' ||
      !Number.isFinite(resp.expiresAt)
    ) {
      throw new Error('Could not renew your session just now. Please try again.');
    }
    return {
      accessToken: resp.accessToken,
      expiresAt: resp.expiresAt,
      // Role and permissions from the new token: a grant or revocation
      // reaches the screen with the next refresh.
      user: profileFrom(resp.accessToken, resp.email),
    };
  },

  setupTotp: (): Promise<{ secret: string; qrCodeUrl: string }> =>
    apiFetch('/admin/auth/totp/setup', { method: 'POST' }),
  verifyTotpSetup: (code: string): Promise<{ enabled: boolean }> =>
    apiFetch('/admin/auth/totp/verify-setup', { method: 'POST', body: { code }, skipAuthRefresh: true }),

  /** Revokes this browser's refresh cookie and clears it (works with an expired access token). */
  logout: (): Promise<{ ok: boolean }> =>
    apiFetch<{ ok: boolean }>('/admin/auth/logout', { method: 'POST', body: {}, skipAuthRefresh: true }),
};
